import { isEmpty } from 'lodash-es';
import {
  crossAssessment,
  filterParameters,
  innerFilterParameters,
  productSaving,
  searchFilter
} from './filter-parameters.inteface';

export interface PurchasePlanSummary {
  category: {
    id: number;
    name: string;
  };
  organization: any;
  numberOfProductsConverted: number;
  numberOfProducts: number;
  currentSpend: number;
  aptitudePlannedSpend: number;
  spendCoverage: any;
  spendBenchmark: {
    continuedSpend: number;
    plannedSpend: number;
    continuedSimilarBuyersBenchmark: number;
    plannedSimilarBuyersBenchmark: number;
    continuedAllBuyersBenchmark: number;
    plannedAllBuyersBenchmark: number;
  };
  additionalValues: any;
  productDetails: any;
  productDeatilList: any;
}

export interface ContractsSummary {
  contractCardDetails: ContractCardDetail[];
}

interface ContractCardDetail {
  categoryId: number;
  categoryName: string;
  buyerId: number;
  buyerName: string;
  sellerId: number;
  sellerName: string;
  contractId: number;
  rfpId: number;
  dateRange: string;
  conversions: number;
  status: string;
  pricingType: string;
  effectiveDate: any;
  duration: number;
  targetMarketShare: number;
  actualMarketShare: number;
  estimatedSpend: number;
  estimatedSavings: number;
  hasBuyerSigned: boolean;
  hasSellerSigned: boolean;
}

export interface ProductListSummary {
  total: number;
  converted: number;
  conversions: Conversion[];
  productDetails: ProductDetail[];
}

interface Conversion {
  isSame: boolean;
  oldProduct: ConversionProduct;
  newProduct: ConversionProduct;
}

interface ConversionProduct {
  incumbentProductId: number;
  productId: number;
  productCatalogNbr: string;
  productDescription: string;
  productSellerName: string;
  productSellerId: number;
  productEachesPrice: number;
}

interface ProductDetail {
  total: number;
  converted: number;
  buyerId: number;
  buyerName: string;
  conversions: Conversion[];
}

export default class PurchasePlansService {
  static $inject = ['$http', '$q'];

  baseUrl = '/aptitude/api/purchaseplans';
  getSearchCountByUnspscId = 0;

  constructor(private $http, private $q) {
    'ngInject';
  }

  getMasterPlanId(purchasePlanId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${purchasePlanId}/masterPlanId`);
  }

  // going to put this here for now...
  getMasterPlanInfo(masterPlanId): ng.IPromise<any> {
    return this.$http.get(
      '/aptitude/buyer/GroupImpactServiceController/getMasterPlanInfo',
      {
        intercept: true,
        params: {
          masterPlanId: masterPlanId,
        },
      },
    );
  }

  getPlans(activePlanId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/plans`, {
      cache: false,
      params: {
        activePlanId: activePlanId,
      },
    });
  }

  setActivePlan(
    activePlanId: number,
    selectedPlanId: number,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/activePlanId`, {
      myPlanId: selectedPlanId,
    });
  }

  hasChangedSincePublished(activePlanId: number): ng.IPromise<any> {
    return this.$http.get(
      `${this.baseUrl}/${activePlanId}/changedSincePublished`,
    );
  }

  getPlanAllocations(planId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${planId}/planallocation`);
  }

  updateAllocateAllToOffer(planId: number, offerId: number) {
    return this.$http.put(
      `${this.baseUrl}/${planId}/offers/${offerId}/allocations?action=ALLOCATE_ALL_TO_OFFER`,
    );
  }

  massAllocate(purchasePlanId: number, offerId: number, productIds) {
    return this.$http.put(
      `${this.baseUrl}/${purchasePlanId}/offers/${offerId}/allocations`,
      {
        productIds,
      },
    );
  }

  lockAndUnlockLocks(
    purchasePlanId: number,
    locked: boolean,
    fromProductIds: any[],
  ) {
    return this.$http.put(
      `${this.baseUrl}/${purchasePlanId}/allocations/lockstatus`,
      {
        locked: locked,
        fromProductIds: fromProductIds,
      },
    );
  }

  updateLockForAllAllocationsInOffer(
    planId: number,
    offerId: number,
    lock: boolean = false,
  ) {
    let action = lock ? 'LOCK_ALL_FOR_OFFER' : 'UNLOCK_ALL_FOR_OFFER';

    return this.$http.put(
      `${this.baseUrl}/${planId}/offers/${offerId}/allocations?action=${action}`,
    );
  }

  // Pass selected: true to add seller or false to remove from plan
  addRemoveSeller(
    planId: number,
    offerId: number,
    selected: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${planId}/offers/${offerId}`, {
      selected: selected,
    });
  }

  getCoverageSummary(planId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${planId}/coverage`);
  }

  getAllProducts(planId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${planId}/products`);
  }

  getOffersTiers(planId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${planId}/offers-tiers`);
  }

  getProductsForUnspsc(planId: number, unspscId: number): ng.IPromise<any> {
    return this.$http.get(
      `${this.baseUrl}/${planId}/unspscs/${unspscId}/products`,
    );
  }

  updateUnspscAllocations(
    planId: number,
    unspscId: number,
    lockAllocations: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${planId}/unspscs/${unspscId}/allocations`,
      {
        lockAllocations,
      },
    );
  }

  updatePlanAllocations(
    planId: number,
    incumbentProductId: number,
    allocations: any[],
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${planId}/products/${incumbentProductId}/allocations`,
      {
        allocations: allocations,
      },
    );
  }

  updateLock(
    planId: number,
    incumbentProductId: number,
    toProductId: number,
    locked: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${planId}/products/${incumbentProductId}/allocations/${toProductId}/status`,
      {
        locked: locked,
      },
    );
  }

  updateOfferTerms(
    planId: number,
    offerId: number,
    pricingType: string,
    duration: number,
    tierId: number,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${planId}/offers/${offerId}/selectedterm`,
      {
        pricingType,
        duration,
        tierId,
      },
    );
  }

  changeTier(
    planId: number,
    offerId: number,
    tierId: number,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${planId}/changetier`, {
      offerId,
      tierId,
      changeTierOperation: 'RE_OPTIMIZE',
    });
  }

  loadProductCatalog(
    purchasePlanId: number,
    offerId: number,
    incumbentProductId: number,
  ): ng.IPromise<any> {
    return this.$http.get(
      `${this.baseUrl}/${purchasePlanId}/offers/${offerId}/products?incumbentProductId=${incumbentProductId}`,
    );
  }

  addCrossReferences(
    purchasePlanId,
    offerId,
    incumbentProductId,
    product,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${purchasePlanId}/products/${incumbentProductId}/crossreferences`,
      {
        offerId,
        crossRefencesAdded: [product.productId],
        crossRefencesRemoved: null,
      },
    );
  }

  removeCrossReferences(
    purchasePlanId,
    offerId,
    incumbentProductId,
    product,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${purchasePlanId}/products/${incumbentProductId}/crossreferences`,
      {
        offerId,
        crossRefencesAdded: null,
        crossRefencesRemoved: [product.productId],
      },
    );
  }

  getSearchCountByUnspsc(
    purchasePlanId: number,
    searchString: string,
    onlyShowUnallocated: boolean,
    onlyShowLocked: boolean,
    onlyShowUnlocked: boolean,
  ): ng.IPromise<any> {
    let params: any = {
      //
    };

    // If the search string is empty, we don't want to send it at all.
    if (searchString.length > 0) {
      params.searchString = searchString;
    }

    if (onlyShowUnallocated) {
      params.isUnallocated = onlyShowUnallocated;
    }

    if (onlyShowLocked) {
      params.isLocked = onlyShowLocked;
    }

    if (onlyShowUnlocked) {
      params.isUnlocked = onlyShowUnlocked;
    }

    // This code short-circuits and skips the call if there's nothing interesting in the search.
    if (
      !isEmpty(searchString) ||
      onlyShowUnallocated ||
      onlyShowLocked ||
      onlyShowUnlocked
    ) {
      let requestId = ++this.getSearchCountByUnspscId;

      return this.$http
        .get(`${this.baseUrl}/${purchasePlanId}/unspscs`, {
          params: params,
        })
        .then((results) => {
          return {
            plannedUnspscs: results.plannedUnspscs,
            mostRecent: requestId == this.getSearchCountByUnspscId,
          };
        });
    } else {
      return this.$q.when({
        plannedUnspscs: [],
        mostRecent: true,
      });
    }
  }

  // Summary Services

  getPurchasePlanSummary(
    purchasePlanId: number,
  ): ng.IPromise<PurchasePlanSummary> {
    return this.$http.get(
      '/aptitude/buyer/BuyerPlanSummaryServiceController/getPlanSummary',
      {
        intercept: true,
        params: {
          purchasePlanId,
        },
      },
    );
  }

  getContractsSummary(purchasePlanId): ng.IPromise<ContractsSummary> {
    return this.$http.get(
      '/aptitude/buyer/BuyerPlanSummaryServiceController/getPlanSummaryContractDetails',
      {
        intercept: true,
        params: {
          purchasePlanId,
        },
      },
    );
  }

  getProductsSummary(purchasePlanId): ng.IPromise<any> {
    return this.$http.get(
      '/aptitude/buyer/BuyerPlanSummaryServiceController/getPlanSummaryProducts',
      {
        intercept: true,
        params: {
          purchasePlanId,
        },
      },
    );
  }

  getProductsSummaryNested(purchasePlanId): ng.IPromise<ProductListSummary> {
    return this.$http.get(
      '/aptitude/buyer/BuyerPlanSummaryServiceController/getPlanSummaryProductList',
      {
        intercept: true,
        params: {
          purchasePlanId,
        },
      },
    );
  }

  updateMixStatus(purchasePlanId: number, mixStatus: string): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${purchasePlanId}/mixstatus`, {
      mixStatus: mixStatus, //UNMODIFIED,MODIFIED,UNCONSIDERED, PUBLISHED 
    });
  }

  getDownloadPlanUrl(purchasePlanId: number, rfpId: number, filters: any): ng.IPromise<any> {

    const filterParameters = this.convertFiltersAsGraphQLParameter(filters);
    return this.$http.post(`${this.baseUrl}/${purchasePlanId}/rfp/${rfpId}/download`, {
      params: filterParameters
    });

  }

  convertFiltersAsGraphQLParameter(filters: any): filterParameters {

    let filter: Array<innerFilterParameters> = [];
    const orStatment = [
      ...(filters.showNegativeSavings || filters.showPositiveSavings) ?
        [{
          or: [
            ...(filters.showNegativeSavings) ? [{ negativeSavings: filters.showNegativeSavings }] : [],
            ...(filters.showPositiveSavings) ? [{ positiveSavings: filters.showPositiveSavings }] : [],
          ]
        }] : [],

      ...(filters.showApproved || filters.showDisapproved || filters.showReview || filters.showNoDecision) ?
        [{
          or: [
            ...(filters.showApproved) ? [{ approved: filters.showApproved }] : [],
            ...(filters.showDisapproved) ? [{ disapproved: filters.showDisapproved }] : [],
            ...(filters.showReview) ? [{ review: filters.showReview }] : [],
            ...(filters.showNoDecision) ? [{ noDecision: filters.showNoDecision }] : [],
          ]
        }] : [],
      ...(filters.showSellerRecommendsPrimary || filters.showSellerRecommendsAlternate || filters.showSellerRecommendsSelfCross
        || filters.showVizientCrossAttribute || filters.showVizientCrossDerived || filters.showBuyerRecommends) ?
        [{
          or: [
            ...(filters.showSellerRecommendsPrimary) ? [{ supplierRecommendsPrimary: filters.showSellerRecommendsPrimary }] : [],
            ...(filters.showSellerRecommendsAlternate) ? [{ supplierRecommendsAlternate: filters.showSellerRecommendsAlternate }] : [],
            ...(filters.showSellerRecommendsSelfCross) ? [{ supplierRecommendsSelfCross: filters.showSellerRecommendsSelfCross }] : [],
            ...(filters.showVizientCrossAttribute) ? [{ vizientCrossAttribute: filters.showVizientCrossAttribute }] : [],
            ...(filters.showVizientCrossDerived) ? [{ vizientCrossDerived: filters.showVizientCrossDerived }] : [],
            ...(filters.showBuyerRecommends) ? [{ buyerRecommends: filters.showBuyerRecommends }] : [],
          ]
        }] : [],
      ...(filters.searchText) ?
        [{
          or: [
            {
              incumbentCatalogNumber: {
                contains: filters.searchText
              }
            },
            {
              incumbentDescription: {
                contains: filters.searchText
              }
            },
            {
              crossCatalogNumber: {
                contains: filters.searchText
              }
            },
            {
              crossDescription: {
                contains: filters.searchText
              }
            }
          ]
        }] : [],
    ];

    filter = [
      ...orStatment.length ? orStatment : [],
      ...(filters.showUnallocated) ? [{ unallocated: filters.showUnallocated }] : [],
      ...(filters.showLocked) ? [{ locked: filters.showLocked }] : [],
      ...(filters.showUnlocked) ? [{ unlocked: filters.showUnlocked }] : [],
      ...(filters.showDiscontinued) ? [{ discontinued: filters.showDiscontinued }] : [],
      ...(filters.showNotDiscontinued) ? [{ notDiscontinued: filters.showNotDiscontinued }] : [],
      ...(filters.showDiscontinuedCrosses) ? [{ discontinuedCrosses: filters.showDiscontinuedCrosses }] : [],
      ...(filters.showNotDiscontinuedCrosses) ? [{ notDiscontinuedCrosses: filters.showNotDiscontinuedCrosses }] : [],
      ...(filters.showUnpriced) ? [{ unpriced: filters.showUnpriced }] : [],
      ...(filters.showNotUnpriced) ? [{ priced: filters.showNotUnpriced }] : [],
      ...(filters.showUnpricedCrosses) ? [{ unpricedCrosses: filters.showUnpricedCrosses }] : [],
      ...(filters.showNotUnpricedCrosses) ? [{ pricedCrosses: filters.showNotUnpricedCrosses }] : [],
      ...(filters.showFormularyPricing) ? [{ formularyPricing: filters.showFormularyPricing }] : [],
      ...(filters.showNonFormularyPricing) ? [{ nonFormularyPricing: filters.showNonFormularyPricing }] : [],
      ...(filters.showIncludedInCompliance) ? [{ includedInCompliance: filters.showIncludedInCompliance }] : [],
      ...(filters.showExcludedFromCompliance) ? [{ excludedFromCompliance: filters.showExcludedFromCompliance }] : [],
      ...(filters.showExcludedFromCompliance) ? [{ excludedFromCompliance: filters.showExcludedFromCompliance }] : [],
      ...(filters.variance) ? [{
        variance: {
          gte: filters.variance
        }
      }] : [],
      ...(filters.incumbentSellers.length) ? [{
        incumbentSellers: {
          oneOf: filters.incumbentSellers
        }
      }] : [],
      ...(filters.crossesBySellers.length) ? [{
        crossSellers: {
          oneOf: filters.crossesBySellers
        }
      }] : [],
      ...(filters.unspscs.length) ? [{
        subcategories: {
          oneOf: filters.unspscs
        }
      }] : [],
    ];

    return {
      ...(filters.sellers.length) && { hideSellers: filters.sellerIDs },
      filter: {
        and: filter
      }
    }
  }
}
